import { useEffect, useState } from "preact/hooks";

// Sprint-008 A2 (design-desktop-phase-a.md §1.1/§7 item 2) — the ONE
// hardcoded breakpoint this spec uses throughout: `≥1024px`, matching the
// plan's "lg" tier. This is the single source of truth gating BOTH swaps
// this spec requires never happen simultaneously: sidebar vs `TabBar`
// (§1.1) and — inside `AdminView`/`AnimalsScreen`/`ProjectsScreen` — the
// desktop-only chrome removals (no `.admin-subtabs` strip, no "back to
// More" button) those screens need once the sidebar replaces `MoreView`
// as their entry point. Every consumer imports THIS hook rather than
// rolling its own `matchMedia` call, so the breakpoint can never drift
// between call sites (styles.css hardcodes the same literal `1024px` in
// its own media queries — see that file's Sprint-008 A2 section).
const DESKTOP_QUERY = "(min-width: 1024px)";

function readIsDesktop(): boolean {
  // SSR/non-DOM guard (not currently exercised — this app has no SSR —
  // but a hook this central is cheap to make safe rather than assume).
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") return false;
  return window.matchMedia(DESKTOP_QUERY).matches;
}

/**
 * `true` at `≥1024px`, `false` below it — reactive to live window resizes
 * (a laptop's external-monitor dock/undock, a browser window drag), not
 * just the width at mount.
 */
export function useIsDesktop(): boolean {
  const [isDesktop, setIsDesktop] = useState(readIsDesktop);

  useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") return;
    const mql = window.matchMedia(DESKTOP_QUERY);
    const handleChange = (event: MediaQueryListEvent) => setIsDesktop(event.matches);
    // `addEventListener`/`removeEventListener` on `MediaQueryList` (not the
    // older deprecated `addListener`) — supported by every browser this
    // app targets (see `styles.css`'s own modern-CSS baseline).
    mql.addEventListener("change", handleChange);
    return () => mql.removeEventListener("change", handleChange);
  }, []);

  return isDesktop;
}
